// Phonebook - personal address book stored in localStorage.
//
// Each contact: { id, name, phones: [...], email?, notes?, tags?, createdAt, updatedAt }
// Phones are stored normalised (0XXXXXXXXX) for fast lookup.

import { create } from 'zustand'

const STORAGE_KEY = 'kaliai_phonebook'

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function save(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)) } catch {}
}

// Normalise to "0XXXXXXXXX" (Israeli format) or keep international as-is.
export function normalisePhone(raw) {
  if (!raw) return ''
  let n = String(raw).replace(/[^\d+]/g, '')
  if (n.startsWith('+972')) n = '0' + n.slice(4)
  else if (n.startsWith('972') && n.length >= 12) n = '0' + n.slice(3)
  else if (n.startsWith('+')) {/* keep international */}
  return n
}

// Pretty-print 0501234567 → 050-123-4567
export function formatPhone(raw) {
  const n = normalisePhone(raw)
  if (n.startsWith('05') && n.length === 10) return `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6)}`
  if (n.length === 10) return `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6)}`
  if (n.length === 9) return `${n.slice(0,2)}-${n.slice(2,5)}-${n.slice(5)}`
  return n
}

const usePhonebookStore = create((set, get) => ({
  contacts: load(),

  add: (contact) => {
    const c = {
      id: 'p-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
      name: contact.name?.trim() || '(ללא שם)',
      phones: (contact.phones || []).map(normalisePhone).filter(Boolean),
      email: contact.email?.trim() || '',
      notes: contact.notes?.trim() || '',
      tags: contact.tags || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    if (c.phones.length === 0) return null  // must have at least one phone
    const next = [c, ...get().contacts]
    save(next); set({ contacts: next })
    return c.id
  },

  update: (id, patch) => {
    const next = get().contacts.map(c => c.id === id
      ? {
          ...c, ...patch,
          phones: patch.phones ? patch.phones.map(normalisePhone).filter(Boolean) : c.phones,
          updatedAt: new Date().toISOString(),
        }
      : c
    )
    save(next); set({ contacts: next })
  },

  remove: (id) => {
    const next = get().contacts.filter(c => c.id !== id)
    save(next); set({ contacts: next })
  },

  // Search by name OR any phone (partial match)
  search: (query) => {
    const q = String(query || '').trim().toLowerCase()
    if (!q) return get().contacts
    const qDigits = q.replace(/[^\d]/g, '')
    return get().contacts.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      (qDigits.length >= 3 && c.phones.some(p => p.includes(qDigits)))
    )
  },

  // Fast lookup: phone → contact (used to label call history / recordings)
  byPhone: (phone) => {
    const n = normalisePhone(phone)
    if (!n) return null
    return get().contacts.find(c => c.phones.includes(n)) || null
  },

  // Bulk import - returns { added, skipped }
  importMany: (rows) => {
    let added = 0, skipped = 0
    const existing = new Set(get().contacts.flatMap(c => c.phones))
    const newContacts = []
    for (const row of rows) {
      const phones = (row.phones || []).map(normalisePhone).filter(Boolean)
      if (phones.length === 0) { skipped++; continue }
      // Skip if all phones already exist
      const allExist = phones.every(p => existing.has(p))
      if (allExist) { skipped++; continue }
      phones.forEach(p => existing.add(p))
      newContacts.push({
        id: 'p-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6) + added,
        name: row.name?.trim() || phones[0],
        phones, email: row.email?.trim() || '', notes: row.notes?.trim() || '',
        tags: row.tags || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      added++
    }
    const next = [...newContacts, ...get().contacts]
    save(next); set({ contacts: next })
    return { added, skipped }
  },

  // Export as CSV
  exportCsv: () => {
    const escape = v => `"${String(v || '').replace(/"/g, '""')}"`
    const header = ['Name', 'Phones', 'Email', 'Notes', 'Tags'].join(',')
    const rows = get().contacts.map(c =>
      [c.name, c.phones.join('; '), c.email, c.notes, (c.tags || []).join('; ')]
        .map(escape).join(',')
    )
    return [header, ...rows].join('\n')
  },

  clearAll: () => { save([]); set({ contacts: [] }) },
}))

// Parse CSV pasted text - very forgiving.
// Each row: Name, Phone(s), Email?, Notes?
// Phones separated by ; or | within a cell. Comma alone treats as new field.
export function parseCsv(text) {
  const out = []
  const lines = text.split(/\r?\n/).filter(l => l.trim())
  if (lines.length === 0) return out

  // Detect if first line is a header
  const first = lines[0].toLowerCase()
  const hasHeader = /name|שם|phone|טלפון|email|מייל/.test(first)
  const startIdx = hasHeader ? 1 : 0

  for (let i = startIdx; i < lines.length; i++) {
    // Simple CSV: handle quoted fields with commas
    const fields = []
    let cur = '', inQ = false
    for (const ch of lines[i]) {
      if (ch === '"') { inQ = !inQ; continue }
      if (ch === ',' && !inQ) { fields.push(cur); cur = ''; continue }
      cur += ch
    }
    fields.push(cur)
    const [name, phones, email, notes] = fields.map(s => s.trim())
    const phoneList = (phones || '').split(/[;|]/).map(p => p.trim()).filter(Boolean)
    // If no name column, treat the first non-phone-looking field as name
    if (phoneList.length > 0) {
      out.push({ name: name || phoneList[0], phones: phoneList, email, notes })
    } else if (name && /\d/.test(name)) {
      // Single column of phones
      out.push({ name: '', phones: [name] })
    }
  }
  return out
}

// vCard parser (basic - handles N, FN, TEL, EMAIL)
export function parseVcf(text) {
  const out = []
  const cards = text.split(/BEGIN:VCARD/i).slice(1)
  for (const card of cards) {
    const body = card.split(/END:VCARD/i)[0]
    const lines = body.split(/\r?\n/)
    let name = '', phones = [], email = ''
    for (const line of lines) {
      const [keyRaw, ...rest] = line.split(':')
      if (!rest.length) continue
      const value = rest.join(':').trim()
      const key = keyRaw.split(';')[0].toUpperCase()
      if (key === 'FN') name = value
      else if (key === 'N' && !name) name = value.split(';').filter(Boolean).reverse().join(' ').trim()
      else if (key === 'TEL') phones.push(value)
      else if (key === 'EMAIL') email = value
    }
    if (phones.length > 0) out.push({ name, phones, email })
  }
  return out
}

export default usePhonebookStore
